// Screenshot import: client-side downscale + POST /v1/import + catalogue slices.
import { config } from "../config";
import { cards, labs, ultimateWeapons, workshopStats } from "./data";

export type ImportKind = "workshop" | "cards" | "labs" | "uws";

export interface CatalogueItem {
  id: string;
  name: string;
  maxLevel: number;
}

export interface ImportMatch {
  id: string;
  level: number;
}

// What the model is allowed to match against, per screen type. Keeping the
// slice small improves accuracy and keeps input tokens (= cost) down.
export function catalogueFor(kind: ImportKind): CatalogueItem[] {
  switch (kind) {
    case "workshop":
      return workshopStats.map((s) => ({ id: s.id, name: s.name, maxLevel: s.maxLevel }));
    case "cards":
      return cards.map((c) => ({ id: c.id, name: c.name, maxLevel: 7 }));
    case "labs":
      return labs.map((l) => ({ id: l.id, name: l.name, maxLevel: l.maxLevel }));
    case "uws":
      return ultimateWeapons.flatMap((uw) =>
        uw.subStats.map((sub) => ({
          id: `${uw.id}/${sub.id}`,
          name: `${uw.name} ${sub.name}`,
          maxLevel: sub.maxLevel,
        })),
      );
  }
}

const MAX_EDGE = 1280;
const JPEG_QUALITY = 0.8;

/** Size-check and downscale: anything over MAX_EDGE px on its longest side is
 *  resized before upload, and everything is re-encoded to JPEG. Returns base64
 *  without the data: prefix. */
export async function downscaleImage(file: File): Promise<string> {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
  const w = Math.round(bitmap.width * scale);
  const h = Math.round(bitmap.height * scale);

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("canvas unavailable");
  ctx.drawImage(bitmap, 0, 0, w, h);
  bitmap.close();

  const dataUrl = canvas.toDataURL("image/jpeg", JPEG_QUALITY);
  return dataUrl.slice(dataUrl.indexOf(",") + 1);
}

export async function importScreenshot(
  idToken: string,
  file: File,
  kind: ImportKind,
): Promise<ImportMatch[]> {
  const image = await downscaleImage(file);
  const res = await fetch(`${config.apiUrl}/v1/import`, {
    method: "POST",
    headers: { Authorization: `Bearer ${idToken}`, "Content-Type": "application/json" },
    body: JSON.stringify({ image, catalogue: catalogueFor(kind) }),
  });
  if (res.status === 429) throw new Error("Daily import limit reached — try again tomorrow.");
  if (!res.ok) throw new Error(`import failed: ${res.status}`);
  const body = (await res.json()) as { matches: ImportMatch[] };
  return body.matches;
}
